/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CluUtteranceEntitySwagger, CluUtteranceSwagger } from 'src/clu/core/services/cluUtteranceSwagger';
import { GptPredictionDecision, GptPredictionUtterance } from 'src/clu/core/services/gpt/cluGptSwagger';
import { CluGptPredictedUtteranceStore } from 'src/clu/core/stores/CluGptPredictedUtteranceStore';
import { CluUtteranceEntityLabel, CluUtteranceStore } from 'src/clu/core/stores/CluUtteranceStore';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';

export const utteranceSwaggerToUtteranceStore = (utteranceSwagger: CluUtteranceSwagger) =>
    new CluUtteranceStore({
        ...utteranceSwagger,
        labeledIntentName: utteranceSwagger.intent,
        dataset: utteranceSwagger.dataset || 'Train',
        tokenizedText: utteranceSwagger.text.split(''),
        labeledEntities: utteranceSwagger.entities?.map(utteranceEntitySwaggerToEntityLabel) ?? []
    });

export const gptPredictedUtteranceSwaggerToGptPredictedUtteranceStore = (utteranceSwagger: GptPredictionUtterance) =>
    new CluGptPredictedUtteranceStore({ ...utteranceSwaggerToUtteranceStore(utteranceSwagger), dataset: 'Generated' });

export const utteranceEntitySwaggerToEntityLabel = (entity: CluUtteranceEntitySwagger) => ({
    entityName: entity.category,
    startCharIndex: entity.offset,
    endCharIndex: entity.offset + entity.length - 1,
    startTokenIndex: entity.offset,
    endTokenIndex: entity.offset + entity.length - 1
});

export const utteranceEntityLabelToEntitySwagger = (entity: CluUtteranceEntityLabel): CluUtteranceEntitySwagger => ({
    category: entity.entityName,
    offset: entity.startCharIndex,
    length: entity.endCharIndex - entity.startCharIndex + 1
});

export const utteranceStoreToUtteranceSwagger = (utteranceStore: CluUtteranceStore): CluUtteranceSwagger => ({
    text: utteranceStore.text,
    dataset: utteranceStore.dataset,
    language: utteranceStore.language,
    intent: utteranceStore.labeledIntentName,
    entities: utteranceStore.labeledEntities.map(utteranceEntityLabelToEntitySwagger)
});

export const gptPredictedUtteranceStoreToGptPredictedUtteranceSwagger = (
    utteranceStore: CluGptPredictedUtteranceStore,
    generalDecision: GptPredictionDecision
): GptPredictionUtterance => ({
    entities: [],
    text: utteranceStore.text,
    dataset: utteranceStore.dataset,
    language: utteranceStore.language,
    updatedText: utteranceStore.updatedText,
    intent: utteranceStore.labeledIntentName,
    updatedIntent: utteranceStore.updatedLabeledIntentName,
    generationDecision: utteranceStore.generationDecision ?? generalDecision,
    predictionDecision: utteranceStore.predictionDecision ?? generalDecision
});

export const gptPredictedUtteranceStoreToUtteranceSwagger = (
    utteranceStore: CluGptPredictedUtteranceStore,
    dataset: DatasetType
): CluUtteranceSwagger => ({
    dataset,
    entities: [],
    language: utteranceStore.language,
    text: utteranceStore.updatedText ?? utteranceStore.text,
    intent: utteranceStore.updatedLabeledIntentName ?? utteranceStore.labeledIntentName
});
